import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ShieldWarning, ClockCounterClockwise, CurrencyCircleDollar, Stamp,
  MagnifyingGlass, Funnel, Eye, Check, X, Warning,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDt = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function AuditCenter() {
  const [activeTab, setActiveTab] = useState('activity');
  const [loading, setLoading] = useState(true);
  const [auditLogs, setAuditLogs] = useState({ total: 0, logs: [] });
  const [financialLogs, setFinancialLogs] = useState([]);
  const [approvalLogs, setApprovalLogs] = useState([]);
  const [boqChanges, setBoqChanges] = useState([]);
  const [finRequests, setFinRequests] = useState([]);
  const [enforcementLogs, setEnforcementLogs] = useState([]);
  const [moduleFilter, setModuleFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const calls = [
        axios.get(`${BACKEND_URL}/api/audit/logs?limit=100${moduleFilter ? `&module=${moduleFilter}` : ''}${searchFilter ? `&action=${searchFilter}` : ''}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/audit/financial-access-logs`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/audit/approval-logs`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/boq-changes`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/financial-access/requests`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/enforcement/overrides?limit=100`, { withCredentials: true }).catch(() => ({ data: [] })),
      ];
      const [auditRes, finLogRes, appRes, boqRes, finReqRes, enfRes] = await Promise.all(calls);
      setAuditLogs(auditRes.data);
      setFinancialLogs(finLogRes.data);
      setApprovalLogs(appRes.data);
      setBoqChanges(boqRes.data);
      setFinRequests(finReqRes.data);
      setEnforcementLogs(Array.isArray(enfRes.data) ? enfRes.data : []);
    } catch { toast.error('Failed to load audit data'); }
    finally { setLoading(false); }
  }, [moduleFilter, searchFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApproveFin = async (id, action) => {
    try {
      await axios.put(`${BACKEND_URL}/api/financial-access/${id}/approve`, { action }, { withCredentials: true });
      toast.success(`Request ${action}ed`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleReviewBoq = async (id, action) => {
    try {
      await axios.put(`${BACKEND_URL}/api/boq-changes/${id}/review`, { action }, { withCredentials: true });
      toast.success(`Change ${action}ed`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const tabs = [
    { key: 'activity', label: 'Activity Logs', icon: ClockCounterClockwise },
    { key: 'boq', label: 'BOQ Changes', icon: ShieldWarning, count: boqChanges.filter(b => b.status === 'pending').length },
    { key: 'financial', label: 'Financial Access', icon: CurrencyCircleDollar, count: finRequests.filter(f => f.status.startsWith('pending')).length },
    { key: 'approvals', label: 'Approval Logs', icon: Stamp },
    { key: 'enforcement', label: 'Enforcement Logs', icon: Warning, count: enforcementLogs.length },
  ];

  const statusStyle = (s) => {
    const m = { pending: 'bg-amber-50 text-amber-700', pending_admin: 'bg-amber-50 text-amber-700', pending_super_admin: 'bg-blue-50 text-blue-700', approved: 'bg-green-50 text-green-700', rejected: 'bg-red-50 text-red-700', reviewed: 'bg-blue-50 text-blue-700' };
    return m[s] || 'bg-zinc-50 text-zinc-600';
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="audit-center">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center gap-2 mb-1">
            <ShieldWarning size={18} className="text-red-700" />
            <h1 className="text-base font-bold text-zinc-900">Audit & Control Center</h1>
          </div>
          <p className="text-xs text-zinc-500">Activity logs, change tracking, financial access control, and approval history.</p>
          <div className="flex gap-0 mt-3 border-b border-zinc-200 -mb-px overflow-x-auto">
            {tabs.map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                className={`flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === t.key ? 'border-red-700 text-red-700' : 'border-transparent text-zinc-500 hover:text-zinc-700'
                }`} data-testid={`audit-tab-${t.key}`}>
                <t.icon size={14} />
                {t.label}
                {t.count > 0 && <span className="bg-red-100 text-red-700 text-[9px] px-1.5 py-0.5 font-bold">{t.count}</span>}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-5">
        {/* Activity Logs */}
        {activeTab === 'activity' && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <select value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)}
                className="border border-zinc-300 px-2 py-1 text-xs bg-white" data-testid="module-filter">
                <option value="">All Modules</option>
                {['boq', 'access_control', 'user_management', 'execution'].map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <div className="relative flex-1">
                <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)}
                  className="rounded-none border-zinc-300 h-8 text-xs pl-7" placeholder="Filter by action..." data-testid="action-filter" />
              </div>
            </div>
            {loading ? <p className="text-xs text-zinc-400 py-4">Loading...</p> : (
              <div className="space-y-1">
                <p className="text-[10px] text-zinc-400">{auditLogs.total} total entries</p>
                {auditLogs.logs.map(l => (
                  <div key={l.id} className="bg-white border border-zinc-200 p-2.5 flex items-start gap-3" data-testid={`audit-log-${l.id}`}>
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 rounded-full bg-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-[10px]">
                        <span className="font-bold text-zinc-700">{l.user_name}</span>
                        <span className="bg-zinc-100 px-1.5 py-0.5 text-zinc-500 font-medium">{l.action}</span>
                        <span className="bg-zinc-50 px-1.5 py-0.5 text-zinc-400">{l.module}</span>
                      </div>
                      <p className="text-[10px] text-zinc-600 mt-0.5 truncate">{l.details}</p>
                      <p className="text-[9px] text-zinc-400 mt-0.5">{fmtDt(l.timestamp)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* BOQ Changes */}
        {activeTab === 'boq' && (
          <div className="space-y-2">
            {boqChanges.length === 0 ? <p className="text-xs text-zinc-400 py-4">No BOQ change requests.</p> : boqChanges.map(c => (
              <div key={c.id} className={`bg-white border p-3 ${c.status === 'pending' ? 'border-amber-200' : 'border-zinc-200'}`} data-testid={`boq-change-${c.id}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-zinc-800">{c.field}</span>
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 ${statusStyle(c.status)}`}>{c.status}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px]">
                      <span className="bg-red-50 text-red-700 px-1.5 py-0.5 line-through">{c.old_value}</span>
                      <span>→</span>
                      <span className="bg-green-50 text-green-700 px-1.5 py-0.5 font-semibold">{c.new_value}</span>
                    </div>
                    <p className="text-[10px] text-zinc-600 mt-1">Reason: {c.reason}</p>
                    <p className="text-[9px] text-zinc-400 mt-0.5">By {c.requested_by_name} ({c.requested_by_role}) · {fmtDt(c.created_at)}</p>
                    {c.reviewed_by_name && <p className="text-[9px] text-zinc-400">{c.status === 'approved' ? 'Approved' : 'Reviewed'} by {c.reviewed_by_name} · {fmtDt(c.reviewed_at)}</p>}
                  </div>
                  {c.status === 'pending' && (
                    <div className="flex gap-1 ml-2">
                      <button onClick={() => handleReviewBoq(c.id, 'approve')} className="p-1.5 bg-green-50 border border-green-200 text-green-700 hover:bg-green-100" data-testid={`boq-approve-${c.id}`}><Check size={12} /></button>
                      <button onClick={() => handleReviewBoq(c.id, 'reject')} className="p-1.5 bg-red-50 border border-red-200 text-red-700 hover:bg-red-100" data-testid={`boq-reject-${c.id}`}><X size={12} /></button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Financial Access */}
        {activeTab === 'financial' && (
          <div className="space-y-2">
            {finRequests.length === 0 ? <p className="text-xs text-zinc-400 py-4">No financial access requests.</p> : finRequests.map(r => (
              <div key={r.id} className={`bg-white border p-3 ${r.status.startsWith('pending') ? 'border-amber-200' : 'border-zinc-200'}`} data-testid={`fin-req-${r.id}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-zinc-800">{r.user_name}</span>
                      <span className="text-[9px] bg-zinc-100 px-1.5 py-0.5 text-zinc-500">{r.user_role}</span>
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 ${statusStyle(r.status)}`}>{r.status.replace(/_/g, ' ')}</span>
                    </div>
                    <p className="text-[10px] text-zinc-700">Project: <strong>{r.project_name}</strong> · Duration: <strong>{r.duration_days} days</strong></p>
                    <p className="text-[10px] text-zinc-600">Reason: {r.reason}</p>
                    <p className="text-[9px] text-zinc-400 mt-0.5">{fmtDt(r.created_at)}</p>
                    {r.admin_approved_by && <p className="text-[9px] text-zinc-400">Admin: {r.admin_approved_by} · {fmtDt(r.admin_approved_at)}</p>}
                    {r.final_approved_by && <p className="text-[9px] text-zinc-400">Final: {r.final_approved_by} · {fmtDt(r.final_approved_at)}</p>}
                    {r.expires_at && <p className="text-[9px] text-amber-600">Expires: {fmtDt(r.expires_at)}</p>}
                  </div>
                  {r.status.startsWith('pending') && (
                    <div className="flex gap-1 ml-2">
                      <button onClick={() => handleApproveFin(r.id, 'approve')} className="p-1.5 bg-green-50 border border-green-200 text-green-700 hover:bg-green-100" data-testid={`fin-approve-${r.id}`}><Check size={12} /></button>
                      <button onClick={() => handleApproveFin(r.id, 'reject')} className="p-1.5 bg-red-50 border border-red-200 text-red-700 hover:bg-red-100" data-testid={`fin-reject-${r.id}`}><X size={12} /></button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Approval Logs */}
        {activeTab === 'approvals' && (
          <div className="space-y-1">
            {approvalLogs.length === 0 ? <p className="text-xs text-zinc-400 py-4">No approval logs.</p> : approvalLogs.map(l => (
              <div key={l.id} className="bg-white border border-zinc-200 p-2.5" data-testid={`approval-log-${l.id}`}>
                <div className="flex items-center gap-2 text-[10px]">
                  <span className="font-bold text-zinc-700">{l.user_name}</span>
                  <span className={`px-1.5 py-0.5 font-medium ${l.action.includes('rejected') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>{l.action}</span>
                  <span className="bg-zinc-50 px-1.5 py-0.5 text-zinc-400">{l.module}</span>
                </div>
                <p className="text-[10px] text-zinc-600 mt-0.5">{l.details}</p>
                <p className="text-[9px] text-zinc-400 mt-0.5">{fmtDt(l.timestamp)}</p>
              </div>
            ))}
          </div>
        )}

        {/* Enforcement Logs */}
        {activeTab === 'enforcement' && (
          <div className="space-y-2" data-testid="enforcement-logs">
            {enforcementLogs.length === 0 ? (
              <div className="text-center py-8">
                <Check size={28} className="mx-auto text-green-400 mb-2" />
                <p className="text-sm text-zinc-500">No enforcement overrides recorded</p>
                <p className="text-xs text-zinc-400 mt-0.5">All enforcement rules are being followed.</p>
              </div>
            ) : enforcementLogs.map(log => (
              <div key={log.id} className="bg-white border-2 border-red-200 p-3" data-testid={`enforcement-log-${log.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Warning size={14} weight="fill" className="text-red-600" />
                    <span className="text-xs font-bold bg-red-100 text-red-700 border border-red-200 px-2 py-0.5">{log.rule_type}</span>
                    <span className="text-[10px] text-zinc-400">{log.entity_type} / {log.entity_id}</span>
                  </div>
                  <div className="text-right">
                    {log.financial_impact > 0 && <span className="text-xs font-bold text-red-700">Rs.{log.financial_impact.toLocaleString('en-IN')}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-zinc-500">Overridden by:</span>
                  <span className="font-bold text-zinc-800">{log.overridden_by_name}</span>
                  <span className="text-[10px] bg-zinc-100 text-zinc-500 px-1.5 py-0.5">{log.overridden_by_role}</span>
                </div>
                <div className="mt-1.5 bg-amber-50 border border-amber-200 p-2">
                  <p className="text-xs text-amber-800"><strong>Reason:</strong> {log.reason}</p>
                </div>
                {log.affected_ids?.length > 0 && (
                  <p className="text-[10px] text-zinc-400 mt-1">Affected: {log.affected_ids.join(', ')}</p>
                )}
                <p className="text-[9px] text-zinc-400 mt-1">{fmtDt(log.timestamp)}</p>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
